// Default email templates for different types

export interface TemplateVariable {
  key: string;
  label: string;
  type: 'text' | 'number' | 'currency' | 'date' | 'html';
}

export interface DefaultTemplate {
  subject: string;
  html: string;
  variables: TemplateVariable[];
}

export const defaultEmailTemplates: Record<
  'dsn' | 'bulletins' | 'editions',
  DefaultTemplate
> = {
  bulletins: {
    subject: 'Bulletins de paie - {{folderName}} - {{monthName}} {{year}}',
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2c3e50;">Bulletins de paie - {{monthName}} {{year}}</h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 5px; margin: 20px 0;">
          <h3 style="color: #495057; margin-top: 0;">Dossier : {{folderName}}</h3>
          <p style="margin-bottom: 10px;"><strong>Période :</strong> {{monthName}} {{year}}</p>
          <p style="margin-bottom: 10px;"><strong>Nombre de bulletins :</strong> {{bulletinsCount}}</p>
        </div>
        
        <div style="margin: 20px 0;">
          <h3 style="color: #495057;">Employés concernés :</h3>
          <ul style="padding-left: 20px;">
            {{employeeList}}
          </ul>
        </div>
        
        <div style="background-color: #e3f2fd; padding: 15px; border-radius: 5px; margin: 20px 0;">
          <p style="margin: 0; color: #1565c0;">
            <strong>Note :</strong> Vous trouverez en pièce jointe les bulletins de paie au format PDF.
          </p>
        </div>
        

      </div>
    `,
    variables: [
      { key: 'folderName', label: 'Nom du dossier', type: 'text' },
      { key: 'monthName', label: 'Mois', type: 'text' },
      { key: 'year', label: 'Année', type: 'number' },
      { key: 'bulletinsCount', label: 'Nombre de bulletins', type: 'number' },
      { key: 'employeeList', label: 'Liste des employés', type: 'html' },
    ],
  },

  dsn: {
    subject:
      'DSN {{monthName}} {{year}} - {{folderName}} (Établissement {{establishmentCode}})',
    html: `
      <body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f1f5f9;">
          <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);">
              
              <!-- Informations principales -->
              <div style="padding: 24px;">
                  <!-- Texte de politesse -->
                  <div style="margin-bottom: 24px;">
                      <p style="margin: 0; color: #1e293b; font-size: 16px; line-height: 1.6;">
                          Bonjour,<br><br>
                          Ci-dessous le récapitulatif de votre Déclaration Sociale Nominative pour {{monthName}} {{year}}.
                      </p>
                  </div>
                  <div style="background: #f8fafc; border-radius: 8px; padding: 20px; margin-bottom: 24px;">
                      <h2 style="color: #1e293b; margin: 0 0 16px 0; font-size: 18px; font-weight: 600;">
                          📁 Informations du dossier
                      </h2>
                      <table style="width: 100%; border-collapse: collapse; border-spacing: 0;">
                          <tr>
                              <td style="width: 50%; padding: 0 8px 0 0; vertical-align: top;">
                                  <p style="margin: 0 0 4px 0; color: #64748b; font-size: 12px; font-weight: 500; text-transform: uppercase;">Dossier</p>
                                  <p style="margin: 0; color: #1e293b; font-weight: 600;">{{folderName}}</p>
                              </td>
                              <td style="width: 50%; padding: 0 0 0 8px; vertical-align: top;">
                                  <p style="margin: 0 0 4px 0; color: #64748b; font-size: 12px; font-weight: 500; text-transform: uppercase;">Code</p>
                                  <p style="margin: 0; color: #2563eb; font-weight: 600;">{{folderCode}}</p>
                              </td>
                          </tr>
                          <tr>
                              <td style="width: 50%; padding: 16px 8px 0 0; vertical-align: top;">
                                  <p style="margin: 0 0 4px 0; color: #64748b; font-size: 12px; font-weight: 500; text-transform: uppercase;">Établissement</p>
                                  <p style="margin: 0; color: #ea580c; font-weight: 600;">{{establishmentCode}}</p>
                              </td>
                              <td style="width: 50%; padding: 16px 0 0 8px; vertical-align: top;">
                                  <p style="margin: 0 0 4px 0; color: #64748b; font-size: 12px; font-weight: 500; text-transform: uppercase;">Effectif</p>
                                  <p style="margin: 0; color: #1e293b; font-weight: 600;">{{employeeCount}} salarié(s)</p>
                              </td>
                          </tr>
                      </table>
                  </div>

                  <!-- Résumé financier -->
                  <div style="margin-bottom: 24px;">
                      <h2 style="color: #1e293b; margin: 0 0 16px 0; font-size: 18px; font-weight: 600;">
                          💰 Résumé financier
                      </h2>
                      <table style="width: 100%; border-collapse: collapse; border-spacing: 0;">
                          <tr>
                              <td style="width: 33.33%; padding: 0 6px 0 0; vertical-align: top;">
                                  <div style="text-align: center; padding: 16px; background: linear-gradient(135deg, #fee2e2, #fecaca); border-radius: 8px; border-left: 4px solid #dc2626;">
                                      <p style="margin: 0 0 4px 0; color: #7f1d1d; font-size: 11px; font-weight: 600; text-transform: uppercase;">Cotisations</p>
                                      <p style="margin: 0; color: #dc2626; font-weight: 700; font-size: 16px;">{{totalContributions}}</p>
                                  </div>
                              </td>
                              <td style="width: 33.33%; padding: 0 3px; vertical-align: top;">
                                  <div style="text-align: center; padding: 16px; background: linear-gradient(135deg, #d1fae5, #a7f3d0); border-radius: 8px; border-left: 4px solid #059669;">
                                      <p style="margin: 0 0 4px 0; color: #064e3b; font-size: 11px; font-weight: 600; text-transform: uppercase;">Net versé</p>
                                      <p style="margin: 0; color: #059669; font-weight: 700; font-size: 16px;">{{netAmount}}</p>
                                  </div>
                              </td>
                              <td style="width: 33.33%; padding: 0 0 0 6px; vertical-align: top;">
                                  <div style="text-align: center; padding: 16px; background: linear-gradient(135deg, #e0e7ff, #c7d2fe); border-radius: 8px; border-left: 4px solid #2563eb;">
                                      <p style="margin: 0 0 4px 0; color: #1e3a8a; font-size: 11px; font-weight: 600; text-transform: uppercase;">Coût global</p>
                                      <p style="margin: 0; color: #2563eb; font-weight: 700; font-size: 16px;">{{totalCost}}</p>
                                  </div>
                              </td>
                          </tr>
                      </table>
                  </div>

                  <!-- Détail des cotisations -->
                  <div style="margin-bottom: 24px;">
                      <h2 style="color: #1e293b; margin: 0 0 16px 0; font-size: 18px; font-weight: 600;">
                          📊 Détail des cotisations
                      </h2>
                      <div style="background: #ffffff; border-radius: 8px; border: 1px solid #e2e8f0; overflow: hidden;">
                          {{contributionsList}}
                      </div>
                  </div>
              </div>

              <!-- Footer -->
              <div style="background: linear-gradient(135deg, #f8fafc, #e2e8f0); padding: 24px; text-align: center; border-radius: 0 0 12px 12px;">
                  <div style="background: #ffffff; padding: 16px; border-radius: 8px; margin-bottom: 16px; border-left: 4px solid #2563eb;">
                      <p style="margin: 0; color: #1e293b; font-size: 14px; font-weight: 500;">
                          📎 Le fichier DSN complet est joint à cet email
                      </p>
                  </div>

              </div>

          </div>
      </body>
      </html>
    `,
    variables: [
      { key: 'folderName', label: 'Nom du dossier', type: 'text' },
      { key: 'folderCode', label: 'Code du dossier', type: 'text' },
      { key: 'monthName', label: 'Mois', type: 'text' },
      { key: 'year', label: 'Année', type: 'number' },
      { key: 'establishmentCode', label: 'Code établissement', type: 'text' },
      { key: 'employeeCount', label: 'Nombre de salariés', type: 'number' },
      {
        key: 'totalContributions',
        label: 'Total des cotisations',
        type: 'currency',
      },
      { key: 'netAmount', label: 'Montant net versé', type: 'currency' },
      { key: 'totalCost', label: 'Coût global', type: 'currency' },
      {
        key: 'contributionsList',
        label: 'Liste des cotisations',
        type: 'html',
      },
      { key: 'currentDate', label: 'Date actuelle', type: 'date' },
    ],
  },

  editions: {
    subject: 'Edition {{editionType}} - {{folderName}} - {{period}}',
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2c3e50;">Edition - {{folderName}}</h2>
        <p><strong>Type d'édition:</strong> {{editionType}}</p>
        <p><strong>Période:</strong> {{period}}</p>
        <div style="background-color: #e3f2fd; padding: 12px; border-radius: 5px; margin: 20px 0;">
          <p style="margin: 0; color: #1565c0;">
            <strong>Note :</strong> Vous trouverez en pièce jointe le PDF de l'édition.
          </p>
        </div>

      </div>
    `,
    variables: [
      { key: 'folderName', label: 'Nom du dossier', type: 'text' },
      { key: 'editionType', label: "Type d'édition", type: 'text' },
      { key: 'period', label: 'Période', type: 'text' },
    ],
  },
};

// Helper function to get template variables
export const getTemplateVariables = (
  templateType: 'dsn' | 'bulletins' | 'editions'
): TemplateVariable[] => {
  return defaultEmailTemplates[templateType]?.variables || [];
};

// Helper function to get default template
export const getDefaultTemplate = (
  templateType: 'dsn' | 'bulletins' | 'editions'
): DefaultTemplate | null => {
  return defaultEmailTemplates[templateType] || null;
};
